import { IconClock, IconOperation } from 'assets/img';
import { Box, Button, styled } from '@mui/material';
import type { ComponentType, SVGProps } from 'react';
import { NodeType } from '../../workflow-editor/hooks/use-sidebar-control.types';

type Props = {
  addDraftNode: (nodeType: NodeType.Wait | NodeType.Action) => void;
};

type NodeLibraryItem = {
  type: NodeType.Wait | NodeType.Action;
  name: string;
  description: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const nodeLibraryItems: NodeLibraryItem[] = [
  {
    type: NodeType.Wait,
    name: 'Timed Delay',
    description:
      'Delay workflow execution until a specified percentage of time remains between shift creation and start time. For example, with 90% and a shift created at 10:20 starting at 10:30, execution continues at 10:21 when 90% of the time remains.',
    Icon: IconClock,
  },
  {
    type: NodeType.Action,
    name: 'Action',
    description: 'Define what should happen next in this workflow.',
    Icon: IconOperation,
  },
];

export const NodeLibrary = (props: Props) => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {nodeLibraryItems.map((item) => {
        return (
          <NodeRow key={item.type}>
            <NodeRowButton variant="outlined" onClick={() => props.addDraftNode(item.type)}>
              <Box display="flex" alignItems="center" gap={1.5} width="100%">
                <IconContainer>
                  <NodeIcon as={item.Icon} />
                </IconContainer>
                <Box display="flex" flexDirection="column" textAlign="left" flex={1}>
                  <Title>{item.name}</Title>
                  <Description>{item.description}</Description>
                </Box>
                <AddIndicator>+</AddIndicator>
              </Box>
            </NodeRowButton>
          </NodeRow>
        );
      })}
    </Box>
  );
};

const NodeRow = styled(Box)({
  position: 'relative',
});

const NodeRowButton = styled(Button)(({ theme }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  borderColor: theme.palette.lwPrimaryAccentDark[20],
  padding: theme.spacing(2, 2),
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  padding: theme.spacing(3),
}));

const NodeIcon = styled('svg')(({ theme }) => ({
  width: 28,
  height: 28,
  color: theme.palette.lwPrimaryAccentDark[100],
  '& path': {
    stroke: theme.palette.lwPrimaryAccentDark[100],
  },
}));

const Title = styled(Box)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.lwPrimaryAccentDark[100],
}));

const Description = styled(Box)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.lwPrimaryAccentDark[60],
}));

const AddIndicator = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 24,
  paddingRight: theme.spacing(2),
  color: theme.palette.lwPrimaryAccentDark[100],
}));
